import React from 'react';
import { Paper, Box, Typography } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { colors } from '@/theme/tokens';

/**
 * KpiCard — a single dashboard metric with optional trend + sparkline.
 * @param title      string
 * @param value      string|number (pre-formatted, e.g. "₹12,400" or "94%")
 * @param icon       MUI icon component
 * @param accent     hex string from tokens (default primary)
 * @param trend      'up' | 'down' | null
 * @param trendLabel string e.g. "+3% vs last week"
 * @param spark      number[] | null — sparkline series
 * @param onClick    optional
 * @param testId     optional data-testid
 */
export default function KpiCard({ title, value, icon: Icon, accent = colors.primary, trend, trendLabel, spark, onClick, testId }) {
  const trendColor = trend === 'up' ? colors.success : trend === 'down' ? colors.error : colors.textSecondary;
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      data-testid={testId}
      sx={{
        p: 2.5, height: '100%', cursor: onClick ? 'pointer' : 'default',
        border: '1px solid', borderColor: 'grey.200', bgcolor: colors.bgPaper,
        transition: 'all 0.2s', '&:hover': onClick ? { boxShadow: 3, transform: 'translateY(-2px)' } : {},
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>{title}</Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: colors.textPrimary, mt: 0.5 }}
            data-testid={testId ? `${testId}-value` : undefined}
          >
            {value}
          </Typography>
        </Box>
        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: `${accent}14`, display: 'flex', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 24, color: accent }} />
        </Box>
      </Box>

      {(trendLabel || (spark && spark.length > 1)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, gap: 1 }}>
          {trendLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: trendColor }}>
              {trend === 'up' && <TrendingUp sx={{ fontSize: 16 }} />}
              {trend === 'down' && <TrendingDown sx={{ fontSize: 16 }} />}
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{trendLabel}</Typography>
            </Box>
          )}
          {spark && spark.length > 1 && (
            <Box sx={{ width: 80, height: 28 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={spark.map((y, x) => ({ x, y }))}>
                  <Line type="monotone" dataKey="y" stroke={accent} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
